import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { CSS_CLASS_PREFIX } from "../constants/css";
import { cssMainSelector } from "../lib/cssSelectors";
import styled from "styled-components";
import MaterialIcon from "./MaterialIcon";
import {
  findPortalDataEntity,
  isPortalDataReady,
  pickPortalDataEntity
} from "../lib/portalData";
import Image from "./Image";
import { selectedMainEntity } from "../lib/mainEntitySelection";
import { MAIN_SELECTION_ENTITIES } from "../constants/mainSelectionEntities";
import MediaContainer from "./MediaContainer";
import PhonePickerButtonContainer from "../containers/PhonePickerButtonContainer";

const PhoneChangerWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    width: 100%;
  }
`;

const ImageFlexWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    align-items: center;
    display: flex;
    height: 120px;
    justify-content: center;
    padding-right: ${p => p.theme.spacing.sm};
  }

  ${p => cssMainSelector(p.theme.tdsId, { mediaSizeName: 
    "outer-lgphone-down" })} {
    height: 65px;
  }
`;

const ImageWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    flex: 1 1 auto;
    text-align: center;

    img {
      max-height: 120px;
      max-width: 120px;
    }
  }

  ${p => cssMainSelector(p.theme.tdsId, { mediaSizeName: 
    "outer-lgphone-down" })} {
    img {
      max-height: 65px;
      max-width: 65px;
    }
  }
`;

const PhoneImageWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
     text-align: center;
      
    .${CSS_CLASS_PREFIX}-material-icon {
      color: ${p => p.theme.phoneChanger.phoneImageColor};
      font-size: 120px;
      max-width: 120px;
    }
  }

  ${p => cssMainSelector(p.theme.tdsId, { mediaSizeName: 
    "outer-lgphone-down" })} {
    .${CSS_CLASS_PREFIX}-material-icon {
      color: ${p => p.theme.phoneChanger.phoneImageColor};
      font-size: 65px;
      max-width: 65px;
    }
  }
`;

const SimOnlyImageWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    .${CSS_CLASS_PREFIX}-material-icon {
      color: ${p => p.theme.phoneChanger.simOnlyImageColor};
      font-size: 90px;
      max-width: 90px;
    }
  }
`;

const Switcher = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    display: block;
    padding-top: ${p => p.theme.spacing.md};
    
    div {
      text-align: center;
    }
  }

  ${p => cssMainSelector(p.theme.tdsId, { mediaSizeName: 
    "outer-lgtablet-down" })} {
    display: none;
  }
`;

const SwitcherMobile = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    display: none;
    position: absolute;
    top: 83px;
  }

  ${p => cssMainSelector(p.theme.tdsId, { mediaSizeName: 
    "outer-lgtablet-down" })} {
    display: block;
  }

  ${p => cssMainSelector(p.theme.tdsId, { mediaSizeName: 
    "outer-lgphone-down" })} {
    top: 28px;
  }
`;

class PhoneChanger extends PureComponent {
  render() {
    try {
      return <MediaContainer>{this.phoneChanger()}</MediaContainer>;
    } catch (e) {
      return null;
    }
  }

  phoneChanger() {
    const { filter, offerType, portalData } = this.props;
    const selectedEntity = selectedMainEntity(offerType, filter);

    if (selectedEntity === MAIN_SELECTION_ENTITIES.simOnly) {
      return PhoneChanger.simOnlyChanger();
    } else {
      const portalDataReady = isPortalDataReady(portalData, offerType);

      if (
        selectedEntity === MAIN_SELECTION_ENTITIES.multiPhone ||
        selectedEntity === MAIN_SELECTION_ENTITIES.unknown
      ) {
        return PhoneChanger.phoneChangerWithMultiPhone();
      } else if (
        selectedEntity === MAIN_SELECTION_ENTITIES.singleBrand &&
        portalDataReady
      ) {
        const brand = pickPortalDataEntity(portalData, "brand", filter.brandId);
        return PhoneChanger.phoneChangerWithSingleBrand(brand);
      } else if (
        selectedEntity === MAIN_SELECTION_ENTITIES.singlePhone &&
        portalDataReady
      ) {
        const phone = pickPortalDataEntity(portalData, "phone", filter.phoneId);
        return PhoneChanger.phoneChangerWithSinglePhone(phone);
      } else if (
        selectedEntity === MAIN_SELECTION_ENTITIES.singlePhoneVariant &&
        portalDataReady
      ) {
        const phoneVariant = pickPortalDataEntity(
          portalData,
          "phoneVariant",
          filter.phoneVariantId
        );
        const phone = findPortalDataEntity(
          portalData,
          "phone",
          phoneVariant.phone.id
        );
        return PhoneChanger.phoneChangerWithSinglePhone(phone);
      }
    }
  }

  static phoneChangerWithMultiPhone() {
    return (
      <PhoneChangerWrapper>
        <SwitcherMobile>
          <PhonePickerButtonContainer small={true} />
        </SwitcherMobile>
        <ImageFlexWrapper>
          <PhoneImageWrapper>
            <MaterialIcon>phone_android</MaterialIcon>
          </PhoneImageWrapper>
        </ImageFlexWrapper>
        <Switcher>
          <PhonePickerButtonContainer />
        </Switcher>
      </PhoneChangerWrapper>
    );
  }

  static phoneChangerWithSingleBrand(brand) {
    return (
      <PhoneChangerWrapper>
        <SwitcherMobile>
          <PhonePickerButtonContainer small={true} />
        </SwitcherMobile>
        <ImageFlexWrapper>
          <ImageWrapper>
            <Image alt={brand.name} src={brand.image80x} responsive />
          </ImageWrapper>
        </ImageFlexWrapper>
        <Switcher>
          <PhonePickerButtonContainer />
        </Switcher>
      </PhoneChangerWrapper>
    );
  }

  static phoneChangerWithSinglePhone(phone) {
    return (
      <PhoneChangerWrapper>
        <SwitcherMobile>
          <PhonePickerButtonContainer small={true} />
        </SwitcherMobile>
        <ImageFlexWrapper>
          <ImageWrapper>
            <Image alt={phone.name} src={phone.image136x136} responsive />
          </ImageWrapper>
        </ImageFlexWrapper>
        <Switcher>
          <PhonePickerButtonContainer />
        </Switcher>
      </PhoneChangerWrapper>
    );
  }

  static simOnlyChanger() {
    return (
      <PhoneChangerWrapper>
        <ImageFlexWrapper>
          <SimOnlyImageWrapper>
            <MaterialIcon>sim_card</MaterialIcon>
          </SimOnlyImageWrapper>
        </ImageFlexWrapper>
      </PhoneChangerWrapper>
    );
  }
}

PhoneChanger.propTypes = {
  filter: PropTypes.object.isRequired,
  offerType: PropTypes.string.isRequired,
  portalData: PropTypes.object.isRequired
};

export default PhoneChanger;
